import {Alert, Button, Divider, Space, Spin, message} from "antd";
import {TTimeslotsPayload} from "../../../../../../../react-query-hooks/public-profile";
import {
  StyledBookingTimeComponent,
  StyledBookingTimeComponentItemWrapper,
} from "../../../../../../../styles/booking/booking";
import {StyledNoUpcomingCallBox} from "../../../../upcoming-calls.style";
import {NewCustomCalendar} from "../../../../../../calendar/custom-calendar";
import {
  TCoachRescheduleCallPayload,
  useCoachRescheduleCall,
} from "../../../../../../../react-query-hooks/booking";
import {useBookingContext} from "../../../../../../../context/booking/booking-context";
import moment from "moment";
import {RescheduleCallFormValues} from "../../../new";
import {useFormContext} from "react-hook-form";
import {
  availableTimes,
  useBookingSchedule,
  useBookingTimeSlots,
  weekdayFromDate,
} from "../../../../../../../features/booking-availability";

export type UpdateStateDateInfoParams = {
  day: number;
  currentMonth: number;
  year: number;
  selectedDateString: Date;
};

export type RescheduleCallCalendarProps = {
  /** The coach whose availability is being shown. */
  mentorId: number;
  serviceId: number;
  bookingReference: string;
  /** The coach's timezone, as recorded on the booking being rescheduled. */
  coachTimezone: string | null;
  onCloseRescheduleCallModalDrawer: () => void;
};

export function CoachRescheduleCallCalendar({
  mentorId,
  serviceId,
  bookingReference,
  coachTimezone,
  onCloseRescheduleCallModalDrawer,
}: RescheduleCallCalendarProps) {
  const timeSlots = useBookingTimeSlots();
  const schedule = useBookingSchedule({
    mentorId,
    serviceId,
    // Empty by design: `fallback` held the schedule that v1 embedded in the
    // booking payload. v2 has standalone availability endpoints and no booking
    // route returns a schedule, so there is nothing left to seed it with.
    fallback: {},
  });
  const coachRescheduleCallMutation = useCoachRescheduleCall();
  const {state: bookingState, dispatch} = useBookingContext();
  const {getValues, reset} = useFormContext<RescheduleCallFormValues>();

  const handleConfirm = () => {
    const payload: TCoachRescheduleCallPayload = {
      bookingReference,
      booking_year: bookingState.booking_year,
      booking_month: bookingState.booking_month,
      booking_date: bookingState.booking_date,
      timeSlot: bookingState.timeSelected as string,
      description: getValues("message"),
    };

    coachRescheduleCallMutation.mutate(payload, {
      onSuccess: () => {
        message.success("Reschedule request sent successfully!");
        dispatch({
          type: "reset-state",
        });
        timeSlots.clearTimeSlots();
        reset();
      },
      onSettled: () => {
        onCloseRescheduleCallModalDrawer();
      },
    });
  };

  function fetchTimeSlots(payload: TTimeslotsPayload) {
    timeSlots.fetchTimeSlots({
      mentorId: Number(payload.mentorId),
      serviceId: Number(payload.serviceId),
      date: payload.date,
      month: payload.month,
      year: payload.year,
      day: weekdayFromDate(new Date(payload.year, payload.month - 1, payload.date)),
      userTimeZone: payload.userTimeZone,
      mentorTimeZone: payload.mentorTimeZone,
    });
  }

  function resetStateTimeSelected() {
    dispatch({
      type: "reset-time-selected",
    });
  }
  function updateStateDateInfo({
    day,
    currentMonth,
    year,
    selectedDateString,
  }: UpdateStateDateInfoParams) {
    dispatch({
      type: "update-date-info",
      payload: {
        booking_date: day,
        booking_month: currentMonth + 1,
        booking_year: year,
        selectedDateString: selectedDateString,
      },
    });
  }
  return (
    <div>
      <h2 style={{fontWeight: 600, fontSize: "1.4rem", color: "#1C1726"}}>
        Select date and time
      </h2>
      <div>
        <div>
          {/* The calendar grid is built once on mount, so it only renders after the
              schedule has resolved. */}
          {schedule.isLoading ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "30vh",
                width: "100%",
              }}
            >
              <Spin size="large" />
            </div>
          ) : schedule.isError ? (
            <Alert
              message="Error"
              description={
                schedule.error?.message ?? "We couldn't load this coach's availability."
              }
              type="error"
              showIcon
            />
          ) : (
            <NewCustomCalendar
              availableWeekdays={schedule.availableWeekdays}
              coachTimezone={coachTimezone}
              customerTimezone={moment.tz.guess(true)}
              mentorId={mentorId}
              serviceId={serviceId}
              fetchTimeSlots={fetchTimeSlots}
              unavailableDates={schedule.unavailableDates}
              resetStateTimeSelected={resetStateTimeSelected}
              updateStateDateInfo={updateStateDateInfo}
            />
          )}
        </div>
        {timeSlots.isLoading && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "65vh",
              width: "100%",
            }}
          >
            <Spin size="large" />
          </div>
        )}
        {timeSlots.isSuccess && timeSlots.data && (
          <StyledBookingTimeComponent id="time-component">
            <div className="time-component-heading">
              {bookingState.selectedDateString.toDateString()}
            </div>
            <Divider style={{marginTop: "1.8rem", marginBottom: "2.4rem"}} />
            <div style={{maxWidth: "31rem"}}>
              <Space size={8} direction="vertical" style={{display: "flex"}}>
                {availableTimes(timeSlots.data.slots).length < 1 && (
                  <StyledNoUpcomingCallBox>
                    <p className="info-text">No time slot available for this date</p>
                  </StyledNoUpcomingCallBox>
                )}
                {availableTimes(timeSlots.data.slots).map((timeSlot: string) => (
                  <StyledBookingTimeComponentItemWrapper key={timeSlot}>
                    <Button
                      block
                      size="small"
                      onClick={() =>
                        dispatch({
                          type: "update-selected-time",
                          payload: timeSlot,
                        })
                      }
                    >
                      {timeSlot}
                    </Button>
                    {bookingState.timeSelected === timeSlot && (
                      <Button
                        size="small"
                        onClick={handleConfirm}
                        loading={coachRescheduleCallMutation.isLoading}
                        type="primary"
                        block
                      >
                        Reschedule
                      </Button>
                    )}
                  </StyledBookingTimeComponentItemWrapper>
                ))}
              </Space>
            </div>
          </StyledBookingTimeComponent>
        )}
      </div>
    </div>
  );
}
